
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import Navbar from "../landingpage/sections/Navbar/index.jsx";
import Footer from "../home/footer";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { getCelebritiesNoAuth } from "../../services/celebrityService";
import { AuthContext } from "../../context/AuthContext";
import LoginModal from "../../auth/login";

const CelebrityList = (props) => {
    const [celebrities, setCelebrities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [selectedCelebrity, setSelectedCelebrity] = useState(null);

    const { isAuthenticated, user } = useContext(AuthContext);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCelebs, setTotalCelebs] = useState(0);

    useEffect(() => {
        document.body.classList.add("map-page");
        loadCelebrities(currentPage);
        return () => document.body.classList.remove("map-page");
    }, [currentPage, user, isAuthenticated]);

    // Clean up old pending bookings on component mount
    useEffect(() => {
        const cleanupStaleBookings = () => {
            const pendingBooking = localStorage.getItem('pendingBooking');
            if (pendingBooking) {
                console.log('Found pending booking from previous session');
            }
        };

        cleanupStaleBookings();
    }, []);

    const loadCelebrities = async (page = 1) => {
        try {
            setLoading(true);
            const response = await getCelebritiesNoAuth(page);

            if (response.message === "Celebrities retrieved successfully") {
                // Get current user info for STRICT filtering
                const currentUserId = user?.userId || user?._id || user?.id;
                const currentUserName = user?.fullName;
                const currentUserPhone = user?.userPhone;
                const currentUserEmail = user?.email;

                console.log('🔍 CELEBLIST FILTERING DEBUG:');
                console.log('User ID:', currentUserId);
                console.log('User Name:', currentUserName);
                console.log('User Phone:', currentUserPhone);
                console.log('User Type:', user?.userType);

                // STRICT FILTERING - Apply only for celebrity users
                let filteredData = response.data;

                if (isAuthenticated && user?.userType === 'celeb') {
                    console.log('🎯 Applying strict filtering for celeb user...');
                    console.log('Total celebs before filtering:', filteredData.length);

                    filteredData = response.data.filter(celeb => {
                        // Check multiple criteria for self-identification
                        const isSameId = celeb._id === currentUserId;
                        const isSameName = currentUserName &&
                            celeb.displayName?.toLowerCase() === currentUserName.toLowerCase();
                        const isSamePhone = currentUserPhone &&
                            celeb.userPhone === currentUserPhone;
                        const isSameEmail = currentUserEmail && celeb.email &&
                            celeb.email.toLowerCase() === currentUserEmail.toLowerCase();

                        const isSelf = isSameId || isSameName || isSamePhone || isSameEmail;

                        if (isSelf) {
                            console.log(`❌ EXCLUDING SELF: ${celeb.displayName} (ID: ${celeb._id})`);
                            console.log(`   ID match: ${isSameId}, Name match: ${isSameName}, Phone match: ${isSamePhone}, Email match: ${isSameEmail}`);
                        }

                        return !isSelf;
                    });

                    console.log('✅ Celebs after filtering:', filteredData.length);
                } else {
                    console.log('👤 Not a celeb user, showing all celebrities');
                }

                const transformedCelebs = filteredData.map((celeb) => {
                    let imageUrl = celeb.picture;
                    if (imageUrl && !imageUrl.startsWith('http') && !imageUrl.startsWith('/')) {
                        imageUrl = 'assets/img/doctor-grid/celeb.jpg';
                    }

                    const hasShoutoutPricing = celeb.shoutout && celeb.shoutout.length > 0;
                    const shoutoutPrice = hasShoutoutPricing ? parseFloat(celeb.shoutout[0].price) : null;

                    return {
                        id: celeb._id,
                        name: celeb.displayName,
                        category: celeb.celebType || 'Entertainer',
                        image: imageUrl,
                        price: shoutoutPrice,
                        hasPricing: hasShoutoutPricing,
                        originalData: celeb
                    };
                });

                setCelebrities(transformedCelebs);
                setTotalPages(response.pagination?.total || 1);
                setTotalCelebs(transformedCelebs.length); // Use filtered count
                setCurrentPage(response.pagination?.current || 1);
            } else {
                setError(response.message || 'Failed to load celebrities');
            }
        } catch (err) {
            console.error('Error loading celebrities:', err);
            const errorMessage =
                err.message ||
                err.response?.data?.message ||
                'Failed to load celebrities';

            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // Check if celebrity is the current user (for UI display)
    const isSelfProfile = (celebrity) => {
        if (!isAuthenticated || user?.userType !== 'celeb') return false;

        const currentUserId = user?.userId || user?._id || user?.id;
        const currentUserName = user?.fullName;
        const currentUserPhone = user?.userPhone;
        const currentUserEmail = user?.email;

        const isSameId = celebrity.id === currentUserId;
        const isSameName = currentUserName &&
            celebrity.name?.toLowerCase() === currentUserName.toLowerCase();
        const isSamePhone = currentUserPhone &&
            celebrity.originalData?.userPhone === currentUserPhone;
        const isSameEmail = currentUserEmail && celebrity.originalData?.email &&
            celebrity.originalData.email.toLowerCase() === currentUserEmail.toLowerCase();

        return isSameId || isSameName || isSamePhone || isSameEmail;
    };

    const handleRequestClick = (celebrity, e) => {
        // STRICT SELF-CHECK before any action
        const isSelf = isSelfProfile(celebrity);

        console.log('🖱️ CELEBLIST CLICK DEBUG:', {
            clickedCeleb: celebrity.name,
            clickedId: celebrity.id,
            currentUserId: user?.userId || user?._id || user?.id,
            currentUserName: user?.fullName,
            isSelf: isSelf,
            userType: user?.userType
        });

        // Prevent self-booking with multiple checks
        if (isAuthenticated && user?.userType === 'celeb' && isSelf) {
            e.preventDefault();
            alert("🚫 You cannot request a shoutout from yourself!");
            console.log('🛑 SELF-BOOKING PREVENTED in CelebrityList!');
            return;
        }

        if (!isAuthenticated) {
            e.preventDefault();

            // Store celebrity data in localStorage for after login
            localStorage.setItem('pendingBooking', JSON.stringify({
                celebId: celebrity.id,
                name: celebrity.name,
                category: celebrity.category,
                image: celebrity.image,
                price: celebrity.price,
                shoutoutPricing: celebrity.originalData.shoutout || []
            }));

            setSelectedCelebrity(celebrity);
            setShowLoginModal(true);
            return;
        }
    };

    if (loading) {
        return (
            <div className="main-wrapper">
                <Navbar {...props} />
                <div className="content">
                    <div className="container">
                        <div className="row justify-content-center">
                            <div className="col-md-6 text-center">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-2">Loading celebrities...</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="main-wrapper">
                <Navbar {...props} />
                <div className="content">
                    <div className="container">
                        <div className="row justify-content-center">
                            <div className="col-md-6 text-center">
                                <div className="alert alert-danger">
                                    <h4>Error Loading Celebrities</h4>
                                    <p>{error}</p>
                                    <button className="btn btn-primary" onClick={() => loadCelebrities(currentPage)}>
                                        Try Again
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div>
                <Navbar {...props} />
                {/* Breadcrumb */}
                <div className="breadcrumb-bar overflow-visible" style={{
                    backgroundColor: 'var(--breadcrumb-bg)',
                    transition: 'background-color 0.3s ease',
                    padding: '40px 0 40px',
                    marginTop: '70px',
                }}>
                    <div className="container">
                        <div className="row align-items-center inner-banner">
                            <div className="col-md-12 col-12 text-center">
                                <nav aria-label="breadcrumb" className="page-breadcrumb">
                                    <ol className="breadcrumb">
                                        <li className="breadcrumb-item">
                                            <Link to="/">
                                                <i className="isax isax-home-15" />
                                            </Link>
                                        </li>
                                        <li className="breadcrumb-item">Celebs</li>
                                        <li className="breadcrumb-item active">Celebrities Grid</li>
                                    </ol>
                                    <h2 className="breadcrumb-title">Celebrities</h2>
                                </nav>
                            </div>
                        </div>
                    </div>
                    <div className="breadcrumb-bg">
                        <ImageWithBasePath src="assets/img/bg/breadcrumb-icon.png" alt="img" className="breadcrumb-bg-03" />
                        <ImageWithBasePath src="assets/img/bg/breadcrumb-icon.png" alt="img" className="breadcrumb-bg-04" />
                    </div>
                </div>
                {/* /Breadcrumb */}

                <div className="content">
                    <div className="container">
                        <div className="row">
                            <div className="col-xl-12">
                                <div className="row align-items-center">
                                    <div className="col-md-6">
                                        <div className="mb-4">
                                            <h3>
                                                Showing <span className="text-secondary">{celebrities.length}</span> Celebrities
                                            </h3>
                                            <p className="text-muted">Page {currentPage} of {totalPages} ({totalCelebs} total)</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    {celebrities.length > 0 ? (
                                        celebrities.map((celebrity) => {
                                            const isSelf = isSelfProfile(celebrity);
                                            const isDisabled = isSelf && isAuthenticated && user?.userType === 'celeb';

                                            return (
                                                <div className="col-xxl-4 col-md-6" key={celebrity.id}>
                                                    <div className="card" style={isDisabled ? { opacity: 0.7 } : {}}>
                                                        {/* Make image area clickable */}
                                                        {celebrity.hasPricing ? (
                                                            <Link
                                                                to={!isDisabled && isAuthenticated ?
                                                                    `/booking?celebId=${celebrity.id}&name=${encodeURIComponent(celebrity.name)}&category=${encodeURIComponent(celebrity.category)}&image=${encodeURIComponent(celebrity.image)}&price=${celebrity.price}&shoutoutPricing=${encodeURIComponent(JSON.stringify(celebrity.originalData.shoutout || []))}`
                                                                    : "#"}
                                                                style={{
                                                                    textDecoration: 'none',
                                                                    color: 'inherit',
                                                                    display: 'block',
                                                                    cursor: isDisabled ? 'not-allowed' : 'pointer'
                                                                }}
                                                                className="card-image-link"
                                                                onClick={(e) => {
                                                                    if (!isAuthenticated) e.preventDefault();
                                                                    if (isDisabled) {
                                                                        e.preventDefault();
                                                                        alert("🚫 You cannot request a shoutout from yourself!");
                                                                    }
                                                                }}
                                                            >
                                                                <div className="card-img card-img-hover position-relative">
                                                                    <img
                                                                        src={celebrity.image}
                                                                        alt={celebrity.name}
                                                                        style={{
                                                                            width: '100%',
                                                                            height: '240px',
                                                                            objectFit: 'cover',
                                                                            objectPosition: 'center',
                                                                        }}
                                                                    />
                                                                    <div className="grid-overlay-item d-flex align-items-center justify-content-between">
                                                                    </div>
                                                                    {isDisabled && (
                                                                        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                                                                            style={{
                                                                                backgroundColor: 'rgba(0,0,0,0.5)',
                                                                                zIndex: 1
                                                                            }}>
                                                                            <span className="badge bg-danger p-2">
                                                                                <i className="fas fa-user-slash me-1"></i>
                                                                                Your Profile
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </Link>
                                                        ) : (
                                                            <div className="card-img card-img-hover position-relative">
                                                                <img
                                                                    src={celebrity.image}
                                                                    alt={celebrity.name}
                                                                    style={{
                                                                        width: '100%',
                                                                        height: '240px',
                                                                        objectFit: 'cover',
                                                                        objectPosition: 'center'
                                                                    }}
                                                                />
                                                                <div className="grid-overlay-item d-flex align-items-center justify-content-between">
                                                                </div>
                                                                {isDisabled && (
                                                                    <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                                                                        style={{
                                                                            backgroundColor: 'rgba(0,0,0,0.5)',
                                                                            zIndex: 1
                                                                        }}>
                                                                        <span className="badge bg-danger p-2">
                                                                            <i className="fas fa-user-slash me-1"></i>
                                                                            Your Profile
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}

                                                        <div className="card-body p-0">
                                                            <div className="d-flex active-bar align-items-center justify-content-between p-3">
                                                                {celebrity.category}
                                                            </div>
                                                            <div className="p-3 pt-0">
                                                                <div className="doctor-info-detail mb-3 pb-3">
                                                                    <h3 className="mb-1">
                                                                        {celebrity.name}
                                                                        {isDisabled && (
                                                                            <span className="badge bg-danger ms-2" title="Your Profile">
                                                                                YOU
                                                                            </span>
                                                                        )}
                                                                    </h3>
                                                                </div>
                                                                <div className="d-flex align-items-center justify-content-between">
                                                                    <div>
                                                                        <p className="mb-1">Shout out fee</p>
                                                                        <h3 className="text-orange">
                                                                            {celebrity.hasPricing ? `$${celebrity.price}` : 'Not Set'}
                                                                        </h3>
                                                                    </div>
                                                                    {celebrity.hasPricing ? (
                                                                        <Link
                                                                            to={!isDisabled && isAuthenticated ?
                                                                                `/booking?celebId=${celebrity.id}&name=${encodeURIComponent(celebrity.name)}&category=${encodeURIComponent(celebrity.category)}&image=${encodeURIComponent(celebrity.image)}&price=${celebrity.price}&shoutoutPricing=${encodeURIComponent(JSON.stringify(celebrity.originalData.shoutout || []))}`
                                                                                : "#"}
                                                                            className={`btn btn-md ${isDisabled ? 'btn-secondary' : 'btn-dark'} d-inline-flex align-items-center rounded-pill`}
                                                                            onClick={(e) => handleRequestClick(celebrity, e)}
                                                                            style={isDisabled ? { cursor: 'not-allowed' } : {}}
                                                                        >
                                                                            <i className="isax isax-card-pos5 me-1" />
                                                                            {isDisabled ? "Your Profile" : "Request"}
                                                                        </Link>
                                                                    ) : (
                                                                        <button
                                                                            className="btn btn-md btn-secondary d-inline-flex align-items-center rounded-pill"
                                                                            disabled
                                                                            title="This celebrity hasn't set up their pricing yet"
                                                                        >
                                                                            <i className="isax isax-card-pos5 me-1" />
                                                                            No Pricing
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div className="col-12 text-center py-5">
                                            <h4>No celebrities found</h4>
                                            <p>No celebrities available on this page</p>
                                        </div>
                                    )}
                                </div>

                                {/* Pagination Controls */}
                                {totalPages > 1 && (
                                    <div className="row">
                                        <div className="col-12">
                                            <div className="pagination-container d-flex justify-content-center mt-4">
                                                <nav>
                                                    <ul className="pagination">
                                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                            <button
                                                                className="page-link"
                                                                onClick={() => setCurrentPage(currentPage - 1)}
                                                                disabled={currentPage === 1}
                                                            >
                                                                Previous
                                                            </button>
                                                        </li>

                                                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                                                            <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                                                                <button
                                                                    className="page-link"
                                                                    onClick={() => setCurrentPage(pageNum)}
                                                                >
                                                                    {pageNum}
                                                                </button>
                                                            </li>
                                                        ))}

                                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                            <button
                                                                className="page-link"
                                                                onClick={() => setCurrentPage(currentPage + 1)}
                                                                disabled={currentPage === totalPages}
                                                            >
                                                                Next
                                                            </button>
                                                        </li>
                                                    </ul>
                                                </nav>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <Footer {...props} />
            </div>

            <LoginModal
                show={showLoginModal}
                onHide={() => {
                    setShowLoginModal(false);
                    setSelectedCelebrity(null);
                }}
            />
        </>
    );
};

export default CelebrityList;

// /* eslint-disable react-hooks/exhaustive-deps */
// import React, { useState, useEffect, useContext } from "react";
// import { Link } from "react-router-dom";
// import Navbar from "../landingpage/sections/Navbar/index.jsx";
// import Footer from "../home/footer";
// import ImageWithBasePath from "../../core/img/imagewithbasebath";
// import { getCelebritiesNoAuth } from "../../services/celebrityService";
// import { AuthContext } from "../../context/AuthContext";
// import LoginModal from "../../auth/login";

// const CelebrityList = (props) => {
//     const [celebrities, setCelebrities] = useState([]);
//     const [loading, setLoading] = useState(true);
//     const [error, setError] = useState(null);
//     const [showLoginModal, setShowLoginModal] = useState(false);
//     const [selectedCelebrity, setSelectedCelebrity] = useState(null);

//     // const { isAuthenticated } = useContext(AuthContext);
//     const { isAuthenticated, user } = useContext(AuthContext);

//     // Pagination state
//     const [currentPage, setCurrentPage] = useState(1);
//     const [totalPages, setTotalPages] = useState(1);
//     const [totalCelebs, setTotalCelebs] = useState(0);

//     useEffect(() => {
//         document.body.classList.add("map-page");
//         loadCelebrities(currentPage);
//         return () => document.body.classList.remove("map-page");
//     }, [currentPage]);

//     // Clean up old pending bookings on component mount
//     useEffect(() => {
//         const cleanupStaleBookings = () => {
//             const pendingBooking = localStorage.getItem('pendingBooking');
//             if (pendingBooking) {
//                 console.log('Found pending booking from previous session');
//             }
//         };

//         cleanupStaleBookings();
//     }, []);

//     const loadCelebrities = async (page = 1) => {
//         try {
//             setLoading(true);
//             const response = await getCelebritiesNoAuth(page);

//             if (response.message === "Celebrities retrieved successfully") {

//                 const filteredData = response.data.filter(celeb => {
//                     // If user is not authenticated or not a celeb, show all
//                     if (!isAuthenticated || user?.userType !== 'celeb') return true;

//                     // If user is a celeb, hide their own profile
//                     return celeb._id !== user._id;
//                 });

//                 const transformedCelebs = filteredData.map((celeb) => {
//                     let imageUrl = celeb.picture;
//                     if (imageUrl && !imageUrl.startsWith('http') && !imageUrl.startsWith('/')) {
//                         imageUrl = 'assets/img/doctor-grid/celeb.jpg';
//                     }

//                     const hasShoutoutPricing = celeb.shoutout && celeb.shoutout.length > 0;
//                     const shoutoutPrice = hasShoutoutPricing ? parseFloat(celeb.shoutout[0].price) : null;

//                     return {
//                         id: celeb._id,
//                         name: celeb.displayName,
//                         category: celeb.celebType || 'Entertainer',
//                         image: imageUrl,
//                         price: shoutoutPrice,
//                         hasPricing: hasShoutoutPricing,
//                         // ADD THIS LINE - store original data for shoutout pricing
//                         originalData: celeb
//                     };
//                 });

//                 setCelebrities(transformedCelebs);
//                 setTotalPages(response.pagination?.total || 1);
//                 setTotalCelebs(filteredData.length);
//                 setCurrentPage(response.pagination?.current || 1);
//             } else {
//                 setError(response.message || 'Failed to load celebrities');
//             }
//         } catch (err) {
//             console.error('Error loading celebrities:', err);
//             const errorMessage =
//                 err.message ||
//                 err.response?.data?.message ||
//                 'Failed to load celebrities';

//             setError(errorMessage);
//         } finally {
//             setLoading(false);
//         }
//     };

//     const handleRequestClick = (celebrity, e) => {
//         if (!isAuthenticated) {
//             e.preventDefault();

//             // Store celebrity data in localStorage for after login
//             localStorage.setItem('pendingBooking', JSON.stringify({
//                 celebId: celebrity.id,
//                 name: celebrity.name,
//                 category: celebrity.category,
//                 image: celebrity.image,
//                 price: celebrity.price,
//                 shoutoutPricing: celebrity.originalData.shoutout || [] // No extra encoding needed
//             }));

//             setSelectedCelebrity(celebrity);
//             setShowLoginModal(true);
//             return;
//         }
//     };


//     if (loading) {
//         return (
//             <div className="main-wrapper">
//                 <Navbar {...props} />
//                 <div className="content">
//                     <div className="container">
//                         <div className="row justify-content-center">
//                             <div className="col-md-6 text-center">
//                                 <div className="spinner-border text-primary" role="status">
//                                     <span className="visually-hidden">Loading...</span>
//                                 </div>
//                                 <p className="mt-2">Loading celebrities...</p>
//                             </div>
//                         </div>
//                     </div>
//                 </div>
//             </div>
//         );
//     }

//     if (error) {
//         return (
//             <div className="main-wrapper">
//                 <Navbar {...props} />
//                 <div className="content">
//                     <div className="container">
//                         <div className="row justify-content-center">
//                             <div className="col-md-6 text-center">
//                                 <div className="alert alert-danger">
//                                     <h4>Error Loading Celebrities</h4>
//                                     <p>{error}</p>
//                                     <button className="btn btn-primary" onClick={() => loadCelebrities(currentPage)}>
//                                         Try Again
//                                     </button>
//                                 </div>
//                             </div>
//                         </div>
//                     </div>
//                 </div>
//             </div>
//         );
//     }

//     return (
//         <>
//             <div>
//                 <Navbar {...props} />
//                 {/* Breadcrumb */}
//                 <div className="breadcrumb-bar overflow-visible" style={{
//                     backgroundColor: 'var(--breadcrumb-bg)',
//                     transition: 'background-color 0.3s ease',
//                     padding: '40px 0 40px',
//                     marginTop: '70px',

//                 }}>
//                     <div className="container">
//                         <div className="row align-items-center inner-banner">
//                             <div className="col-md-12 col-12 text-center">
//                                 <nav aria-label="breadcrumb" className="page-breadcrumb">
//                                     <ol className="breadcrumb">
//                                         <li className="breadcrumb-item">
//                                             <Link to="/">
//                                                 <i className="isax isax-home-15" />
//                                             </Link>
//                                         </li>
//                                         <li className="breadcrumb-item">Celebs</li>
//                                         <li className="breadcrumb-item active">Celebrities Grid</li>
//                                     </ol>
//                                     <h2 className="breadcrumb-title">Celebrities</h2>
//                                 </nav>
//                             </div>
//                         </div>
//                     </div>
//                     <div className="breadcrumb-bg">
//                         <ImageWithBasePath src="assets/img/bg/breadcrumb-icon.png" alt="img" className="breadcrumb-bg-03" />
//                         <ImageWithBasePath src="assets/img/bg/breadcrumb-icon.png" alt="img" className="breadcrumb-bg-04" />
//                     </div>
//                 </div>
//                 {/* /Breadcrumb */}

//                 <div className="content">
//                     <div className="container">
//                         <div className="row">
//                             <div className="col-xl-12">
//                                 <div className="row align-items-center">
//                                     <div className="col-md-6">
//                                         <div className="mb-4">
//                                             <h3>
//                                                 Showing <span className="text-secondary">{celebrities.length}</span> Celebrities
//                                             </h3>
//                                             <p className="text-muted">Page {currentPage} of {totalPages} ({totalCelebs} total)</p>
//                                         </div>
//                                     </div>
//                                 </div>
//                                 <div className="row">
//                                     {celebrities.length > 0 ? (
//                                         celebrities.map((celebrity) => (

//                                             <div className="col-xxl-4 col-md-6" key={celebrity.id}>
//                                                 <div className="card">
//                                                     {/* Make image area clickable */}
//                                                     {celebrity.hasPricing ? (
//                                                         <Link
//                                                             to={isAuthenticated ? `/booking?celebId=${celebrity.id}&name=${encodeURIComponent(celebrity.name)}&category=${encodeURIComponent(celebrity.category)}&image=${encodeURIComponent(celebrity.image)}&price=${celebrity.price}&shoutoutPricing=${encodeURIComponent(JSON.stringify(celebrity.originalData.shoutout || []))}` : "#"}
//                                                             style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
//                                                             className="card-image-link"
//                                                             onClick={(e) => !isAuthenticated && e.preventDefault()}
//                                                         >
//                                                             <div className="card-img card-img-hover">
//                                                                 <img
//                                                                     src={celebrity.image}
//                                                                     alt={celebrity.name}
//                                                                     style={{
//                                                                         width: '100%',
//                                                                         height: '240px',
//                                                                         objectFit: 'cover',
//                                                                         objectPosition: 'center',
//                                                                         cursor: 'pointer'
//                                                                     }}
//                                                                 />
//                                                                 <div className="grid-overlay-item d-flex align-items-center justify-content-between">
//                                                                 </div>
//                                                             </div>
//                                                         </Link>
//                                                     ) : (
//                                                         <div className="card-img card-img-hover">
//                                                             <img
//                                                                 src={celebrity.image}
//                                                                 alt={celebrity.name}
//                                                                 style={{
//                                                                     width: '100%',
//                                                                     height: '240px',
//                                                                     objectFit: 'cover',
//                                                                     objectPosition: 'center'
//                                                                 }}
//                                                             />
//                                                             <div className="grid-overlay-item d-flex align-items-center justify-content-between">
//                                                             </div>
//                                                         </div>
//                                                     )}

//                                                     <div className="card-body p-0">
//                                                         <div className="d-flex active-bar align-items-center justify-content-between p-3">
//                                                             {celebrity.category}
//                                                         </div>
//                                                         <div className="p-3 pt-0">
//                                                             <div className="doctor-info-detail mb-3 pb-3">
//                                                                 <h3 className="mb-1">
//                                                                     {celebrity.name}
//                                                                 </h3>
//                                                             </div>
//                                                             <div className="d-flex align-items-center justify-content-between">
//                                                                 <div>
//                                                                     <p className="mb-1">Shout out fee</p>
//                                                                     <h3 className="text-orange">
//                                                                         {celebrity.hasPricing ? `$${celebrity.price}` : 'Not Set'}
//                                                                     </h3>
//                                                                 </div>
//                                                                 {celebrity.hasPricing ? (
//                                                                     <Link
//                                                                         to={isAuthenticated ? `/booking?celebId=${celebrity.id}&name=${encodeURIComponent(celebrity.name)}&category=${encodeURIComponent(celebrity.category)}&image=${encodeURIComponent(celebrity.image)}&price=${celebrity.price}&shoutoutPricing=${JSON.stringify(celebrity.originalData.shoutout || [])}` : "#"}
//                                                                         className="btn btn-md btn-dark d-inline-flex align-items-center rounded-pill"
//                                                                         onClick={(e) => handleRequestClick(celebrity, e)}
//                                                                     >
//                                                                         <i className="isax isax-card-pos5 me-1" />
//                                                                         Request
//                                                                     </Link>
//                                                                 ) : (
//                                                                     <button
//                                                                         className="btn btn-md btn-secondary d-inline-flex align-items-center rounded-pill"
//                                                                         disabled
//                                                                         title="This celebrity hasn't set up their pricing yet"
//                                                                     >
//                                                                         <i className="isax isax-card-pos5 me-1" />
//                                                                         No Pricing
//                                                                     </button>
//                                                                 )}
//                                                             </div>
//                                                         </div>
//                                                     </div>
//                                                 </div>
//                                             </div>
//                                         ))
//                                     ) : (
//                                         <div className="col-12 text-center py-5">
//                                             <h4>No celebrities found</h4>
//                                             <p>No celebrities available on this page</p>
//                                         </div>
//                                     )}
//                                 </div>

//                                 {/* Pagination Controls */}
//                                 {totalPages > 1 && (
//                                     <div className="row">
//                                         <div className="col-12">
//                                             <div className="pagination-container d-flex justify-content-center mt-4">
//                                                 <nav>
//                                                     <ul className="pagination">
//                                                         <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
//                                                             <button
//                                                                 className="page-link"
//                                                                 onClick={() => setCurrentPage(currentPage - 1)}
//                                                                 disabled={currentPage === 1}
//                                                             >
//                                                                 Previous
//                                                             </button>
//                                                         </li>

//                                                         {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
//                                                             <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
//                                                                 <button
//                                                                     className="page-link"
//                                                                     onClick={() => setCurrentPage(pageNum)}
//                                                                 >
//                                                                     {pageNum}
//                                                                 </button>
//                                                             </li>
//                                                         ))}

//                                                         <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
//                                                             <button
//                                                                 className="page-link"
//                                                                 onClick={() => setCurrentPage(currentPage + 1)}
//                                                                 disabled={currentPage === totalPages}
//                                                             >
//                                                                 Next
//                                                             </button>
//                                                         </li>
//                                                     </ul>
//                                                 </nav>
//                                             </div>
//                                         </div>
//                                     </div>
//                                 )}
//                             </div>
//                         </div>
//                     </div>
//                 </div>
//                 <Footer {...props} />
//             </div>

//             <LoginModal
//                 show={showLoginModal}
//                 onHide={() => {
//                     setShowLoginModal(false);
//                     setSelectedCelebrity(null);
//                 }}
//             />
//         </>
//     );
// };

// export default CelebrityList;

